import { useEffect, useState } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useDebounce, useLatest } from 'ahooks'
import { Alert, App, Button, Pagination, Select } from 'antd'
import {
  documentAmendmentSchema,
  type DocumentAmendment,
  type EnterpriseSelection,
  type ServiceEnterpriseDraft
} from '@super-mega/server/contracts/order'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'
import { EnterpriseDocumentsEditor } from './enterprise-draft-editor.tsx'
import { EnterpriseEditorModal } from './enterprise-editor-modal.tsx'
import { EMPTY_DOCUMENTS } from './enterprise-labels.ts'
import { enterpriseModalProps } from './enterprise-modal-layout.ts'
import { EnterpriseSummary } from './enterprise-summary.tsx'

export type EnterpriseSelectorProps = {
  customerId?: number
  purpose?: 'order.rental.read' | 'order.service.read'
  value?: EnterpriseSelection
  onChange?: (value: EnterpriseSelection) => void
  defaultKind?: ServiceEnterpriseDraft['kind']
  disabled?: boolean
  id?: string
  onPendingChange?: (pending: boolean) => void
}

export function EnterpriseSelector({
  customerId,
  purpose = 'order.rental.read',
  value,
  onChange,
  defaultKind = 'prospectiveCompany',
  disabled,
  id,
  onPendingChange
}: EnterpriseSelectorProps) {
  const trpc = useTRPC()
  const access = useAccess()
  const { modal, message } = App.useApp()
  const [search, setSearch] = useState({ keyword: '', current: 1 })
  const keyword = useDebounce(search.keyword, { wait: 300 })
  const [creating, setCreating] = useState(false)
  const [draftGeneration, setDraftGeneration] = useState(0)
  const [draft, setDraft] = useState<ServiceEnterpriseDraft>({
    customerId: customerId ?? 0,
    kind: defaultKind,
    displayName: ''
  })
  const [amending, setAmending] = useState(false)
  const [amendment, setAmendment] = useState<DocumentAmendment>(EMPTY_DOCUMENTS)
  const [ready, setReady] = useState(true)
  const [amendmentError, setAmendmentError] = useState<string>()
  const [amendmentGeneration, setAmendmentGeneration] = useState(0)
  const pendingCallback = useLatest(onPendingChange)
  useEffect(() => {
    const report = pendingCallback.current
    report?.(creating || amending)
    return () => report?.(false)
  }, [creating, amending, pendingCallback])
  const options = useQuery({
    ...trpc.lookup.subjects.queryOptions({
      purpose,
      customerId: customerId ?? 0,
      keyword,
      current: search.current,
      pageSize: 20
    }),
    enabled: Boolean(customerId),
    placeholderData: keepPreviousData
  })
  const subjectId = value?.mode === 'existing' ? value.subjectId : undefined
  const selected = useQuery({
    ...trpc.order.subjects.detail.queryOptions({ subjectId: subjectId ?? 0 }),
    enabled: Boolean(subjectId) && access.can('subject.read')
  })
  const selectedOption = useQuery({
    ...trpc.lookup.subjects.queryOptions({ purpose, customerId: customerId ?? 0, selectedId: subjectId }),
    enabled: Boolean(subjectId)
  })
  const blocked = disabled || !customerId
  const replaceSelection = (next: EnterpriseSelection) => {
    const changedDraft = value?.mode === 'new' || (value?.mode === 'existing' && value.amendment)
    if (changedDraft) {
      modal.confirm({
        title: '更换服务对象？',
        content: '当前已确认的服务对象草稿或待提交资料会从本次订单移除。',
        onOk: () => onChange?.(next)
      })
    } else {
      onChange?.(next)
    }
  }
  const rows = [...(selectedOption.data?.data ?? []), ...(options.data?.data ?? [])].filter(
    (row, index, all) => row.customerId === customerId && all.findIndex((other) => row.id === other.id) === index
  )
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select
          id={id}
          className="min-w-0 flex-1"
          aria-label="服务对象"
          disabled={blocked}
          value={subjectId}
          placeholder={customerId ? '搜索客户名下服务对象，或暂不关联' : '请先选择客户'}
          loading={options.isFetching}
          options={rows.map((row) => ({ value: row.id, label: row.name, disabled: row.isArchived }))}
          showSearch={{ filterOption: false, onSearch: (text) => setSearch({ keyword: text, current: 1 }) }}
          popupRender={(menu) => (
            <>
              {menu}
              <div className="p-2">
                <Pagination
                  size="small"
                  current={search.current}
                  pageSize={20}
                  total={options.data?.total ?? 0}
                  showSizeChanger={false}
                  onChange={(current) => setSearch((previous) => ({ ...previous, current }))}
                />
              </div>
            </>
          )}
          onChange={(nextId: number) => {
            const row = rows.find((item) => item.id === nextId)
            if (row && row.customerId === customerId && !row.isArchived) {
              replaceSelection({ mode: 'existing', subjectId: row.id, expectedVersion: row.version })
            }
          }}
        />
        <Authorized permissions={['subject.create']}>
          <Button
            disabled={blocked}
            onClick={() => {
              setDraft(
                value?.mode === 'new'
                  ? value.draft
                  : { customerId: customerId ?? 0, kind: defaultKind, displayName: '' }
              )
              setDraftGeneration((previous) => previous + 1)
              setCreating(true)
            }}
          >
            {value?.mode === 'new' ? '编辑服务对象草稿' : '新增候选服务对象'}
          </Button>
        </Authorized>
        <Button
          disabled={blocked || !value || value.mode === 'none'}
          onClick={() => replaceSelection({ mode: 'none' })}
        >
          暂不关联
        </Button>
      </div>
      {options.isError && (
        <Alert
          type="error"
          title={options.error.message}
          action={<Button onClick={() => void options.refetch()}>重试</Button>}
        />
      )}
      {selected.isError && (
        <Alert
          type="error"
          title={selected.error.message}
          action={<Button onClick={() => void selected.refetch()}>重试</Button>}
        />
      )}
      {(!value || value.mode === 'none') && (
        <p className="m-0 text-sm text-slate-500">暂不关联服务对象，可在资料明确后补充。</p>
      )}
      {value?.mode === 'new' && (
        <Alert
          type="info"
          showIcon
          title={`${value.draft.displayName} · 待随订单创建`}
          description="此服务对象及资料仍为本地草稿，订单最终提交后才保存。"
        />
      )}
      {selected.data && value?.mode === 'existing' && (
        <>
          <EnterpriseSummary enterprise={selected.data} />
          {selected.data.version !== value.expectedVersion && (
            <Alert type="warning" title="企业资料已更新；当前待提交选择已保留，请重新选择并核对最新资料。" />
          )}
          {selected.data.customerId !== customerId && (
            <Alert type="error" title="所选服务对象不属于当前客户，请重新选择" />
          )}
          {selected.data.kind === 'person' && (
            <Alert type="warning" title="这是历史个人对象，请人工核对；不会自动转换为企业。" />
          )}
          {value.amendment && <Alert type="info" title="资料修改待随订单保存" />}
          <Authorized permissions={['subject.material.update']}>
            <Button
              disabled={blocked || selected.data.isArchived || selected.data.kind === 'person'}
              onClick={() => {
                setAmendment(value.amendment ?? EMPTY_DOCUMENTS)
                setReady(true)
                setAmendmentError(undefined)
                setAmendmentGeneration((generation) => generation + 1)
                setAmending(true)
              }}
            >
              修改资料
            </Button>
          </Authorized>
        </>
      )}
      <EnterpriseEditorModal
        key={draftGeneration}
        open={creating}
        onOpenChange={setCreating}
        value={draft}
        onChange={setDraft}
        customerLocked
        retainOnCancel={false}
        pending={disabled}
        submitText="确认用于本次订单"
        onConfirm={(next) => {
          if (next.customerId !== customerId) {
            void message.error('客户已变更，请重新创建候选服务对象')
            return false
          }
          onChange?.({ mode: 'new', draft: next })
          setCreating(false)
          return true
        }}
      />
      {selected.data && (
        <ModalForm
          key={amendmentGeneration}
          open={amending}
          onOpenChange={setAmending}
          title="修改本次订单资料"
          width="64rem"
          modalProps={{ ...enterpriseModalProps, mask: { closable: false } }}
          submitter={{
            searchConfig: { submitText: '确认用于本次订单' },
            submitButtonProps: { disabled: !ready || disabled }
          }}
          onFinish={async () => {
            if (!ready || value?.mode !== 'existing') {
              return false
            }
            const parsed = documentAmendmentSchema.safeParse(amendment)
            if (!parsed.success) {
              setAmendmentError(parsed.error.issues[0]?.message)
              return false
            }
            onChange?.({ ...value, amendment: parsed.data })
            setAmending(false)
            return true
          }}
        >
          <p className="mb-4 text-sm text-slate-500">确认后回填本单，保存订单时生效；取消则保留原资料。</p>
          {amendmentError && <Alert type="error" title={amendmentError} />}
          <EnterpriseDocumentsEditor
            value={amendment}
            onChange={setAmendment}
            onReadyChange={setReady}
            current={selected.data}
            disabled={disabled}
          />
        </ModalForm>
      )}
    </div>
  )
}
